import React, { Fragment, memo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Button } from 'src/components/shared/Button'
import { FormikFileInput } from 'src/components/shared/form/formik/FormikFileInput'
import {
  BUSINESS_UPLOAD_FILE_SIZE_LIMIT,
  HISTORICAL_INVOICE_ACCEPT_TYPES,
  HISTORICAL_INVOICE_CSV_COLUMNS,
  HISTORICAL_INVOICE_FILE_CAPTION,
} from 'src/constants/oatfi-business/constants'
import type { HistoricalInvoicesFormValues } from './HistoricalInvoicesForm.interface'

interface Props {
  isUploading: boolean
}

const HistoricalInvoicesFormContentComponent = ({ isUploading }: Props) => {
  return (
    <Fragment>
      {/* The column names are shown because a rejected upload otherwise gives no clue what the
          file should have contained — the server validates every row against
          AdminPartnerHistoricalInvoiceDto and returns only the first failure. This is the
          required column list, not a downloadable template (which would be fabricated data). */}
      <Typography variant="h6" css={tw`mb-1 text-shades-400`}>
        Required columns
      </Typography>
      <Typography variant="body2" css={tw`mb-1 text-shades-700`}>
        {HISTORICAL_INVOICE_CSV_COLUMNS.join(', ')}
      </Typography>
      <Typography variant="body2" css={tw`mb-4 text-shades-400`}>
        <code>paymentDate</code> and <code>description</code> are optional. Dates are ISO strings.{' '}
        <code>amount</code> is a whole number of cents.
      </Typography>

      {/* The size cap is a product decision rather than a wire constraint — the API documents
          none — and the caption is built from the same constant so the copy cannot drift from
          the check. */}
      <div css={tw`mb-4`}>
        <FormikFileInput<HistoricalInvoicesFormValues>
          name="file"
          acceptTypes={HISTORICAL_INVOICE_ACCEPT_TYPES}
          caption={HISTORICAL_INVOICE_FILE_CAPTION}
          fileSizeLimit={BUSINESS_UPLOAD_FILE_SIZE_LIMIT}
          inputDataTestId="historical-invoices-file-input"
        />
      </div>

      <div css={tw`flex flex-row justify-end`}>
        <Button
          type="submit"
          size="small"
          color="dark"
          className={tw`px-4`}
          loading={isUploading}
          disabled={isUploading}
          data-testid="historical-invoices-submit"
        >
          Upload invoices
        </Button>
      </div>
    </Fragment>
  )
}

export const HistoricalInvoicesFormContent = memo(HistoricalInvoicesFormContentComponent)
